/**
 * Luxe · Cabinet3DBuilder (Phase 2B-1).
 *
 * Deterministic conversion of one canonical Cabinet item into a
 * Three.js Group. Every geometry, every division and every material
 * slot follows canonical DesignScene state — never invents a value.
 *
 * The group hierarchy is:
 *   cabinet:{id}
 *     └── carcass (with left/right end-profile silhouette extrusion)
 *     └── top
 *     └── bay:{i}:{id}
 *           └── door | drawer:{j}:{id} | shelf:{j}:{id} | back
 *
 * Each userData carries stable canonical IDs so AI segmentation and
 * later render diagnostics can map pixels back to DesignScene state.
 */
import * as THREE from "three";
import { mmToM, distToWorld } from "./coordinateAdapter";
import { resolveSurface } from "./Material3DResolver";

// ── Basic PBR-neutral material factory ─────────────────────────
// Real texture loading lives in a future Material3DTextureLoader
// module. For 2B-1 we colour-code by material_id/source so viewer +
// tests can confirm assignment correctness without heavy asset I/O.
const _hashColour = (mid) => {
  if (!mid) return 0x888888;
  let h = 0;
  for (let i = 0; i < mid.length; i++) h = (h * 31 + mid.charCodeAt(i)) | 0;
  const r = ((h & 0xff0000) >> 16) & 0xff;
  const g = ((h & 0x00ff00) >> 8)  & 0xff;
  const b = (h & 0x0000ff)          & 0xff;
  // Push toward mid-lightness so black items never appear invisible.
  return (Math.max(60, r) << 16) | (Math.max(60, g) << 8) | Math.max(60, b);
};
function _material(resolved) {
  if (!resolved) return new THREE.MeshStandardMaterial({ color: 0xaaaaaa, roughness: 0.75 });
  return new THREE.MeshStandardMaterial({
    color: _hashColour(resolved.material_id),
    roughness: 0.6,
    metalness: 0.0,
  });
}

// ── End-profile silhouette extrusion ───────────────────────────
// Builds the flat SHAPE of a cabinet's front face taking left/right
// end profiles into account. Then extrudes it depth-wise to form the
// physical carcass volume.
function _cabinetShape(widthMm, heightMm, leftProfile, rightProfile) {
  const w = mmToM(widthMm), h = mmToM(heightMm);
  const shape = new THREE.Shape();
  const lOff = _endOffsetM(leftProfile,  heightMm);
  const rOff = _endOffsetM(rightProfile, heightMm);
  // Bottom-left
  const startX = leftProfile && leftProfile.kind === "semicircle" ? 0 : 0;
  const startY = 0;
  shape.moveTo(lOff, startY);
  shape.lineTo(w - rOff, startY);
  _addEndArc(shape, "right", rightProfile, w, h);
  shape.lineTo(lOff, h);
  _addEndArc(shape, "left", leftProfile, 0, h);
  return shape;
}
function _endOffsetM(p, heightMm) {
  if (!p || (p.kind || "square") === "square") return 0;
  if (p.kind === "radius")     return mmToM(p.radius_mm || 0);
  if (p.kind === "semicircle") return mmToM((heightMm || 0) / 2);
  if (p.kind === "bevel")      return mmToM(p.bevel_depth_mm || 0);
  return 0;
}
function _addEndArc(shape, side, p, xEdge, heightM) {
  if (!p || p.kind === "square") {
    shape.lineTo(xEdge, side === "right" ? heightM : 0);
    return;
  }
  const half = heightM / 2;
  if (p.kind === "radius") {
    const r = mmToM(p.radius_mm || 0);
    if (side === "right") {
      shape.absarc(xEdge - r, 0, r, -Math.PI / 2, 0, false);  // bottom-right quarter
      shape.lineTo(xEdge, heightM - r);
      shape.absarc(xEdge - r, heightM, r, 0, Math.PI / 2, false);  // top-right quarter
      shape.lineTo(xEdge - r, heightM);
    } else {
      shape.absarc(xEdge + r, heightM, r, Math.PI / 2, Math.PI, false);
      shape.lineTo(xEdge, r);
      shape.absarc(xEdge + r, 0, r, Math.PI, 3 * Math.PI / 2, false);
      shape.lineTo(xEdge + r, 0);
    }
    return;
  }
  if (p.kind === "semicircle") {
    if (side === "right") {
      shape.absarc(xEdge - half, half, half, -Math.PI / 2, Math.PI / 2, false);
      shape.lineTo(xEdge - half, heightM);
    } else {
      shape.absarc(xEdge + half, half, half, Math.PI / 2, 3 * Math.PI / 2, false);
      shape.lineTo(xEdge + half, 0);
    }
    return;
  }
  if (p.kind === "bevel") {
    const d = mmToM(p.bevel_depth_mm || 0);
    if (side === "right") {
      shape.lineTo(xEdge, half);
      shape.lineTo(xEdge - d, heightM);
    } else {
      shape.lineTo(xEdge, half);
      shape.lineTo(xEdge + d, 0);
    }
    return;
  }
}

// ── Bay geometry helpers ────────────────────────────────────────
function _computeBayWidths(cfg, cabinetWidthMm) {
  const bays = cfg?.bays || [];
  if (!bays.length) return [];
  if (cfg.layout_mode === "even_split") {
    const each = cabinetWidthMm / bays.length;
    return bays.map(() => each);
  }
  const total = bays.reduce((s, b) => s + (Number(b.width_mm) || 0), 0);
  if (total <= 0) return bays.map(() => cabinetWidthMm / bays.length);
  const scale = cabinetWidthMm / total;
  return bays.map((b) => (Number(b.width_mm) || 0) * scale);
}

// ── Public: build a single cabinet as a THREE.Group ─────────────
export function buildCabinetGroup(item, { pinsByMaterialId = {} } = {}) {
  const group = new THREE.Group();
  group.name = `cabinet:${item.id}`;
  group.userData = {
    item_id: item.id,
    component_type: item.type,
    canonical: { x: item.x, y: item.y, z: item.z, w: item.width, h: item.height, d: item.depth },
  };
  const w = mmToM(item.width),  h = mmToM(item.height);
  const d = mmToM(item.depth || 600);
  // Position: canonical (x, y, z) is top-left-front-relative-to-wall.
  // Translate so the cabinet's canonical bottom-left corner lands at
  // world (x_m, y_m, 0). Z=0 is the wall plane; cabinet projects
  // toward the room (+Z).
  group.position.set(mmToM(item.x), mmToM(item.y), mmToM(item.z || 0));
  // ── Carcass (extruded silhouette) ─────────────────────────
  const shape = _cabinetShape(item.width, item.height, item.left_end_profile, item.right_end_profile);
  const geom = new THREE.ExtrudeGeometry(shape, {
    depth: d, bevelEnabled: false, curveSegments: 32,
  });
  const carcassResolved = resolveSurface({ item, scope: "carcass", pinsByMaterialId });
  const carcass = new THREE.Mesh(geom, _material(carcassResolved));
  carcass.name = `cabinet:${item.id}:carcass`;
  carcass.userData = { scope: "carcass", resolved: carcassResolved };
  group.add(carcass);
  // ── Top (thin slab across the top surface) ───────────────
  const topResolved = resolveSurface({ item, scope: "top", pinsByMaterialId });
  const topGeom = new THREE.BoxGeometry(w, distToWorld(20), d);
  const top = new THREE.Mesh(topGeom, _material(topResolved));
  top.position.set(w / 2, h + distToWorld(10), d / 2);
  top.name = `cabinet:${item.id}:top`;
  top.userData = { scope: "top", resolved: topResolved };
  group.add(top);
  // ── Bays ─────────────────────────────────────────────────
  const cfg = item.cabinet_config || { layout_mode: "even_split", bays: [] };
  const widths = _computeBayWidths(cfg, item.width);
  let cx = 0;
  (cfg.bays || []).forEach((bay, bi) => {
    const bwM = mmToM(widths[bi] || 0);
    const bayGroup = new THREE.Group();
    bayGroup.name = `cabinet:${item.id}:bay:${bi}:${bay.id || ""}`;
    bayGroup.userData = { item_id: item.id, bay_index: bi, bay_id: bay.id };
    bayGroup.position.set(cx, 0, 0);
    if (bay.kind === "door") {
      const frontRes = resolveSurface({ item, scope: "front", bayIndex: bi, pinsByMaterialId });
      const doorGeom = new THREE.BoxGeometry(bwM * 0.98, h * 0.98, mmToM(18));
      const door = new THREE.Mesh(doorGeom, _material(frontRes));
      door.position.set(bwM / 2, h / 2, d - mmToM(9));
      door.name = `${bayGroup.name}:door`;
      door.userData = { scope: "door", hinge: bay.hinge || "right", resolved: frontRes };
      bayGroup.add(door);
    } else if (bay.kind === "drawer") {
      const drawers = Array.isArray(bay.drawers) ? bay.drawers : [];
      if (drawers.length && bay.drawer_layout === "custom") {
        let yy = 0;
        drawers.forEach((dr, dj) => {
          const drH = mmToM(Number(dr.height_mm) || 0);
          const drRes = resolveSurface({
            item, scope: "front", bayIndex: bi,
            drawerId: dr.id, pinsByMaterialId,
          });
          const drGeom = new THREE.BoxGeometry(bwM * 0.98, drH * 0.96, mmToM(18));
          const drawer = new THREE.Mesh(drGeom, _material(drRes));
          drawer.position.set(bwM / 2, yy + drH / 2, d - mmToM(9));
          drawer.name = `${bayGroup.name}:drawer:${dj}:${dr.id || ""}`;
          drawer.userData = { scope: "drawer", drawer_id: dr.id, resolved: drRes };
          bayGroup.add(drawer);
          yy += drH;
        });
      } else {
        // Equal-drawer fallback.
        const n = Math.max(1, Number(bay.drawer_count) || 1);
        const each = h / n;
        for (let j = 0; j < n; j++) {
          const drGeom = new THREE.BoxGeometry(bwM * 0.98, each * 0.96, mmToM(18));
          const drawer = new THREE.Mesh(drGeom, _material(resolveSurface({
            item, scope: "front", bayIndex: bi, pinsByMaterialId,
          })));
          drawer.position.set(bwM / 2, j * each + each / 2, d - mmToM(9));
          drawer.name = `${bayGroup.name}:drawer:${j}`;
          drawer.userData = { scope: "drawer" };
          bayGroup.add(drawer);
        }
      }
    } else if (bay.kind === "open") {
      const backRes    = resolveSurface({ item, scope: "back",         bayIndex: bi, pinsByMaterialId });
      const shelfDefault = resolveSurface({ item, scope: "shelf_default", bayIndex: bi, pinsByMaterialId });
      // Back panel
      const backGeom = new THREE.BoxGeometry(bwM * 0.98, h * 0.98, mmToM(6));
      const back = new THREE.Mesh(backGeom, _material(backRes));
      back.position.set(bwM / 2, h / 2, mmToM(3));
      back.name = `${bayGroup.name}:back`;
      back.userData = { scope: "back", resolved: backRes };
      bayGroup.add(back);
      // Shelves (custom or equal)
      if (Array.isArray(bay.shelves_config) && bay.shelves_config.length && bay.shelf_layout === "custom") {
        bay.shelves_config.forEach((s, sj) => {
          const yM = mmToM(Number(s.y_mm) || 0);
          const res = s.material_id
            ? resolveSurface({
                item, scope: "shelf_default", bayIndex: bi,
                shelfId: s.id, pinsByMaterialId,
              })
            : shelfDefault;
          const sh = new THREE.Mesh(
            new THREE.BoxGeometry(bwM * 0.94, mmToM(20), d * 0.9),
            _material(res),
          );
          sh.position.set(bwM / 2, yM, d / 2);
          sh.name = `${bayGroup.name}:shelf:${sj}:${s.id || ""}`;
          sh.userData = { scope: "shelf", shelf_id: s.id, canonical_y_mm: s.y_mm, resolved: res };
          bayGroup.add(sh);
        });
      } else {
        const n = Math.max(0, Number(bay.shelves) || 0);
        for (let j = 0; j < n; j++) {
          const yM = h * (j + 1) / (n + 1);
          const sh = new THREE.Mesh(
            new THREE.BoxGeometry(bwM * 0.94, mmToM(20), d * 0.9),
            _material(shelfDefault),
          );
          sh.position.set(bwM / 2, yM, d / 2);
          sh.name = `${bayGroup.name}:shelf:${j}`;
          sh.userData = { scope: "shelf" };
          bayGroup.add(sh);
        }
      }
    }
    group.add(bayGroup);
    cx += bwM;
  });
  return group;
}
